<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #actions>
      <v-btn
        class="mr-2"
        color="primary"
        @click="showNewSection"
      >
        <v-icon>mdi-plus</v-icon>
        {{ $t('button.add') }}
      </v-btn>
    </template>
    <template #content>
      <!--  -->
      <input-dialog
        v-model="newSectionName"
        :title="$t('title.add_section')"
        :label-title="$t('label.section_name')"
        :visible="newSectionDialog"
        rules="required|min:2|max:255"
        @confirm="add"
        @close="newSectionDialog = false"
      />
      <!--  -->
      <validation-observer ref="observer">
        <!-- Table -->
        <v-data-table
          v-reorder-table
          class="clickable-table"
          :headers="headers"
          :items="sections"
          disable-pagination
          mobile-breakpoint="0"
          hide-default-footer
          @click:row="edit"
          @sorted="changeOrder"
        >
          <!-- Reorder Hanlde -->
          <template #item.id>
            <v-icon
              class="reorder-handle"
              small
            >
              mdi-arrow-up-down
            </v-icon>
          </template>

          <!-- Custom Online prop Rendering -->
          <template #item.isOnline="props">
            <v-chip
              v-if="props.item.usingWeekdaysAndTimes"
              color="success"
              small
            >
              {{ $t('label.online') }}
            </v-chip>
            <v-chip
              v-if="!props.item.usingWeekdaysAndTimes"
              color="gray"
              small
            >
              {{ $t('label.offline') }}
            </v-chip>
          </template>
        </v-data-table>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import Sortable from 'sortablejs';
import { isModelEqualToModel, IModel, cloneModel } from '@/model/model-utils';
import { performSaveAction, confirmLeaveWithoutSaving } from '@/services/vue-utils';
import Tab from '@/model/Tab';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import InputDialog from '@/components/dialogs/InputDialog.vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';

export default Vue.extend({
  name: 'Sections',
  components: { InputDialog, SettingsForm },
  mixins: [SettingsBreadcrumbs],
  data() {
    return {
      newSectionDialog: false,
      newSectionName: '',
      sections: [] as Tab[],
      oldSections: [] as Tab[],
      unwatch: Function as (() => void),
      breadcrumbs: [],
      headers: [
        {
          text: '',
          width: '32',
          value: 'id',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$i18n.t('label.tab_name'),
          value: 'name',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$i18n.t('label.online'),
          width: '32',
          value: 'isOnline',
          align: 'start',
          sortable: false,
        },
      ],
    };
  },
  computed: { },
  created() {
    this.unwatch = this.$tstore.watch((state, getters) => getters.isLoaded, (newValue, oldValue) => {
      this.reset();
    });
  },
  beforeDestroy() {
    this.unwatch();
  },
  mounted() {
    this.reset();
  },
  methods: {
    changeOrder(event: Sortable.SortableEvent) {
      if (event.oldIndex === undefined || event.newIndex === undefined) return;
      const movedItem = this.sections.splice(event.oldIndex, 1)[0];
      this.sections.splice(event.newIndex, 0, movedItem);
      this.save();
    },
    async save() {
      const entities = this.sections;
      await performSaveAction(
        this.$refs.observer,
        async () => this.$tstore.dispatch('sendTabs', { entities }),
      );
      this.reset();
    },
    showNewSection() {
      this.reset();
      this.newSectionDialog = true;
      this.newSectionName = '';
    },
    async add(newSectionName: string) {
      const section = new Tab();
      section.name = newSectionName;
      const entities = this.$tstore.state.settings.tabs.map((o) => cloneModel(o));
      entities.push(section);

      const ok = await performSaveAction(
        this.$refs.observer,
        async () => this.$tstore.dispatch('sendTabs', { entities }),
      );
      if (ok) {
        this.reset();
        const newSection = this.sections.filter((o) => o.name === newSectionName).pop();
        if (newSection) this.edit(newSection);
      } else {
        this.newSectionDialog = true;
      }
    },
    edit(section: Tab) {
      this.$router.push(`/settings/sections/${section.id}`);
    },
    reset() {
      const { tabs } = this.$tstore.state.settings;
      this.oldSections = tabs;
      this.sections = tabs.map((o) => cloneModel(o));
    },
  },
});
</script>
